import React, { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { SessionContext } from "../context/sessionContext";
import SurveyFlowHeader from "../components/SurveyFlowHeader";
import {
  savePendingGenerateDraft,
  markStepReached,
  getMaxStepReached,
} from "../utils/surveyFlowStorage";

function EditIcon() {
  return (
    <img src="/json-media/icons/edit.svg" alt="Edit" width={16} height={16} />
  );
}

function DeleteIcon() {
  return (
    <img
      src="/json-media/icons/delete.svg"
      alt="Delete"
      width={16}
      height={16}
    />
  );
}

function GripIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="4" cy="2.5" r="1.2" fill="currentColor" />
      <circle cx="4" cy="7" r="1.2" fill="currentColor" />
      <circle cx="4" cy="11.5" r="1.2" fill="currentColor" />
      <circle cx="10" cy="2.5" r="1.2" fill="currentColor" />
      <circle cx="10" cy="7" r="1.2" fill="currentColor" />
      <circle cx="10" cy="11.5" r="1.2" fill="currentColor" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M7 1.5V12.5M1.5 7H12.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path
        d="M1.5 1.5L11.5 11.5M11.5 1.5L1.5 11.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path
        d="M6.5 10.5V2.5M2.5 6.5L6.5 2.5L10.5 6.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path
        d="M6.5 2.5V10.5M2.5 6.5L6.5 10.5L10.5 6.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M7 1.2L13.3 12.2C13.6 12.75 13.2 13.4 12.55 13.4H1.45C0.8 13.4 0.4 12.75 0.7 12.2L7 1.2Z"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      <path
        d="M7 5.3V8.3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <circle cx="7" cy="10.6" r="0.9" fill="currentColor" />
    </svg>
  );
}

const QUESTION_TYPES = [
  { value: "single_select", label: "Single Choice" },
  { value: "multi_select", label: "Multiple Choice" },
  { value: "ranking", label: "Ranking" },
  { value: "rating_1_10", label: "Rating Scale" },
  { value: "open_text", label: "Open Text" },
  { value: "numeric", label: "Numeric" },
];

const OPTION_BEARING_TYPES = ["single_select", "multi_select", "ranking"];
const MIN_OPTIONS_BY_TYPE = {
  single_select: 2,
  ranking: 2,
  multi_select: 3,
};

function typeLabel(type) {
  return QUESTION_TYPES.find((t) => t.value === type)?.label || type;
}

function getRemoveDisabledReason(q, optionCount) {
  const remaining = optionCount - 1;
  const min = MIN_OPTIONS_BY_TYPE[q.type];
  if (min !== undefined && remaining < min) {
    return `${typeLabel(q.type)} needs at least ${min} choices`;
  }
  if (
    q.type === "multi_select" &&
    q.maxSelections &&
    q.maxSelections > remaining
  ) {
    return `Lower "Select up to" (${q.maxSelections}) before removing more choices`;
  }
  return null;
}

function RadioAnswers({ options }) {
  return (
    <>
      {options.map((opt, i) => (
        <div key={i} className="review-option">
          <span className="review-radio"></span>
          <span className="review-option__label">{opt}</span>
        </div>
      ))}
    </>
  );
}

function CheckboxAnswers({ options }) {
  return (
    <>
      {options.map((opt, i) => (
        <div key={i} className="review-option">
          <span className="review-checkbox" />
          <span className="review-option__label">{opt}</span>
        </div>
      ))}
    </>
  );
}

function RankAnswers({ items }) {
  return (
    <div className="rank-list">
      {items.map((item, i) => (
        <div key={i} className="rank-row">
          <span className="rank-drag-handle rank-drag-handle--static">
            <GripIcon />
          </span>
          <span className="rank-num">{i + 1}</span>
          <span className="rank-label rank-label--static">{item}</span>
        </div>
      ))}
    </div>
  );
}

function RatingAnswer({ scale = 10 }) {
  return (
    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
      {Array.from({ length: scale }, (_, i) => (
        <div key={i} className="rating-dot">
          {i + 1}
        </div>
      ))}
    </div>
  );
}

function TextAnswer({ placeholder }) {
  return (
    <textarea
      className="review-textarea"
      placeholder={placeholder || "Enter your answer"}
      rows={3}
      readOnly
    />
  );
}

function NumericAnswer() {
  return (
    <input
      className="numeric-input"
      type="text"
      placeholder="Enter a number"
      readOnly
    />
  );
}

function Toast({ toast, onDismiss }) {
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  if (!toast) return null;

  return (
    <div className={`user-toast user-toast--${toast.type}`} role="alert">
      {toast.message}
    </div>
  );
}

function AddQuestionButton({ onAdd, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="q-add-question-wrap" ref={wrapRef}>
      <button
        type="button"
        className="q-add-question-btn"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
      >
        <PlusIcon /> {disabled ? "Adding…" : "Add Question"}
      </button>

      {open && !disabled && (
        <div className="q-type-picker">
          {QUESTION_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              className="q-type-picker__option"
              onClick={() => {
                onAdd(t.value);
                setOpen(false);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionCard({
  q,
  isEditing,
  isDragging,
  isSavingEdit,
  isDeleting,
  deleteDisabledReason,
  editError,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onChangeField,
  onOptionTextChange,
  onAddOption,
  onRemoveOption,
  onMoveRankItem,
  dragHandlers,
}) {
  const optionTexts = (q.options || []).map((o) => o.text);
  const hasOptions = OPTION_BEARING_TYPES.includes(q.type);

  return (
    <div
      className={`q-card${isEditing ? " q-card--editing" : ""}${isDragging ? " q-card--dragging" : ""}`}
      draggable={!isEditing}
      {...(!isEditing ? dragHandlers : {})}
    >
      <div className="q-card__header">
        <div className="q-card__header-left">
          {!isEditing && (
            <span className="q-card__grip" title="Drag to reorder">
              <GripIcon />
            </span>
          )}
          {isEditing ? (
            <input
              className="q-edit-field"
              value={q.text}
              onChange={(e) => onChangeField("text", e.target.value)}
              placeholder="Question text"
            />
          ) : (
            <span className="q-card__text">{q.text}</span>
          )}
        </div>

        {!isEditing && (
          <div className="q-card__header-actions">
            <button
              className="q-card__edit-btn"
              onClick={onStartEdit}
              title="Edit question"
            >
              <EditIcon />
            </button>
            <button
              className="q-card__delete-btn"
              onClick={onDelete}
              disabled={isDeleting || !!deleteDisabledReason}
              title={deleteDisabledReason || "Delete question"}
            >
              <DeleteIcon />
            </button>
          </div>
        )}
      </div>

      {q.type === "multi_select" &&
        (isEditing ? (
          <div className="q-edit-inline-setting">
            <label>Select up to</label>
            <input
              type="number"
              min={1}
              max={optionTexts.length || undefined}
              className="q-edit-number"
              value={q.maxSelections || ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (!raw) {
                  onChangeField("maxSelections", null);
                  return;
                }
                const clamped = Math.min(Number(raw), optionTexts.length);
                onChangeField("maxSelections", clamped);
              }}
              placeholder="No limit"
            />
          </div>
        ) : (
          q.maxSelections && (
            <p
              style={{
                fontSize: "0.78rem",
                color: "#888",
                marginBottom: "0.5rem",
              }}
            >
              Select up to {q.maxSelections}
            </p>
          )
        ))}

      {q.type === "rating_1_10" &&
        (isEditing ? (
          <div className="q-edit-inline-setting">
            <label>Scale</label>
            <div className="q-scale-toggle">
              <button
                type="button"
                className={`q-scale-toggle__btn${(q.scale || 10) === 5 ? " q-scale-toggle__btn--active" : ""}`}
                onClick={() => onChangeField("scale", 5)}
              >
                5 Point
              </button>
              <button
                type="button"
                className={`q-scale-toggle__btn${(q.scale || 10) === 10 ? " q-scale-toggle__btn--active" : ""}`}
                onClick={() => onChangeField("scale", 10)}
              >
                10 Point
              </button>
            </div>
          </div>
        ) : (
          <p
            style={{
              fontSize: "0.78rem",
              color: "#888",
              marginBottom: "0.5rem",
            }}
          >
            {q.scale || 10}-point scale
          </p>
        ))}

      {q.type === "open_text" && isEditing && (
        <input
          className="q-edit-field"
          style={{ marginBottom: "0.75rem" }}
          value={q.placeholder || ""}
          onChange={(e) => onChangeField("placeholder", e.target.value)}
          placeholder="Placeholder text shown to respondents"
        />
      )}

      {isEditing && hasOptions && (
        <div className="q-choice-list">
          {optionTexts.map((opt, i) => {
            const removeReason = getRemoveDisabledReason(q, optionTexts.length);
            return (
              <div className="q-choice-row" key={i}>
                {q.type === "ranking" && (
                  <span className="rank-num rank-num--sm">{i + 1}</span>
                )}
                <input
                  className="q-choice-input"
                  value={opt}
                  onChange={(e) => onOptionTextChange(i, e.target.value)}
                  placeholder={`Choice ${i + 1}`}
                />
                {q.type === "ranking" && (
                  <div className="rank-reorder-btns">
                    <button
                      type="button"
                      className="rank-reorder-btn"
                      disabled={i === 0}
                      onClick={() => onMoveRankItem(i, i - 1)}
                      title="Move up"
                    >
                      <ArrowUpIcon />
                    </button>
                    <button
                      type="button"
                      className="rank-reorder-btn"
                      disabled={i === optionTexts.length - 1}
                      onClick={() => onMoveRankItem(i, i + 1)}
                      title="Move down"
                    >
                      <ArrowDownIcon />
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="q-choice-remove"
                  onClick={() => onRemoveOption(i)}
                  disabled={!!removeReason}
                  title={removeReason || "Remove choice"}
                >
                  <RemoveIcon />
                </button>
              </div>
            );
          })}
          <button
            type="button"
            className="q-add-choice-btn"
            onClick={onAddOption}
          >
            <PlusIcon /> Add Choice
          </button>
        </div>
      )}

      {!isEditing && q.type === "single_select" && (
        <RadioAnswers options={optionTexts} />
      )}
      {!isEditing && q.type === "multi_select" && (
        <CheckboxAnswers options={optionTexts} />
      )}
      {!isEditing && q.type === "ranking" && (
        <RankAnswers items={optionTexts} />
      )}
      {!isEditing && q.type === "rating_1_10" && (
        <RatingAnswer scale={q.scale || 10} />
      )}
      {!isEditing && q.type === "open_text" && (
        <TextAnswer placeholder={q.placeholder} />
      )}
      {!isEditing && q.type === "numeric" && <NumericAnswer />}

      {isEditing && editError && (
        <div className="q-card__inline-error">
          <WarningIcon />
          <span>{editError}</span>
        </div>
      )}

      {isEditing && (
        <div className="q-edit-footer">
          <div className="q-edit-footer__actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onCancelEdit}
              disabled={isSavingEdit}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={onSaveEdit}
              disabled={isSavingEdit}
            >
              {isSavingEdit ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const SPEC_STORAGE_KEY = "surveySpecifications";

function readLocalSpecs() {
  try {
    return JSON.parse(localStorage.getItem(SPEC_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function persistLocalSurveyPayload(payload) {
  try {
    const saved = readLocalSpecs();
    localStorage.setItem(
      SPEC_STORAGE_KEY,
      JSON.stringify({
        ...saved,
        requestId: payload.requestId,
        surveyPayload: payload,
      }),
    );
  } catch (_) {}
}

export default function SurveyGenerated({ onBack, onContinue }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, getUserId } = useContext(SessionContext);

  const [maxStepReached] = useState(() => {
    markStepReached("generated");
    return getMaxStepReached() || "generated";
  });

  const stateSurveyData = location.state?.surveyData;
  const resumeRequestId = location.state?.resumeRequestId;

  const localSpecs = !stateSurveyData ? readLocalSpecs() : {};
  const localPayload = !stateSurveyData ? localSpecs.surveyPayload : null;
  const localRequestId = !stateSurveyData ? localSpecs.requestId : null;
  const effectiveResumeId = resumeRequestId || localRequestId;

  const [surveyData, setSurveyData] = useState(
    stateSurveyData || localPayload || null,
  );
  const [isResuming, setIsResuming] = useState(
    !!effectiveResumeId && !stateSurveyData && !localPayload,
  );
  const [resumeError, setResumeError] = useState(null);
  const requestId = surveyData?.requestId || effectiveResumeId;

  const [survey, setSurvey] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [draggedQuestionId, setDraggedQuestionId] = useState(null);

  const [toast, setToast] = useState(null);
  const [addingQuestionType, setAddingQuestionType] = useState(null);
  const [deletingQuestionId, setDeletingQuestionId] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [questionErrors, setQuestionErrors] = useState({});

  const editBackupRef = useRef(null);
  const savedTitleRef = useRef("");

  useEffect(() => {
    if (stateSurveyData || localPayload || !effectiveResumeId) return;

    let cancelled = false;
    (async () => {
      try {
        const token = Cookies.get("token");
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/generated/survey/${effectiveResumeId}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", token },
          },
        );
        const data = await res.json();
        if (!res.ok || !data.success || !data.survey) {
          throw new Error(
            data.message || "Couldn't load your generated survey.",
          );
        }
        const payload = { ...data, requestId: effectiveResumeId };
        if (!cancelled) {
          setSurveyData(payload);
          persistLocalSurveyPayload(payload);
        }
      } catch (err) {
        if (!cancelled) {
          setResumeError(err.message || "Couldn't load your generated survey.");
        }
      } finally {
        if (!cancelled) setIsResuming(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const raw = surveyData?.survey;
    if (!raw) return;

    const cloned = {
      ...raw,
      sections: (raw.sections || []).map((sec) => ({
        ...sec,
        questions: [...(sec.questions || [])]
          .sort((a, b) => a.order - b.order)
          .map((q) => ({
            ...q,
            options: q.options ? q.options.map((o) => ({ ...o })) : q.options,
          })),
      })),
    };
    setSurvey(cloned);
    savedTitleRef.current = cloned.title || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyData]);

  if (isResuming) {
    return (
      <div className="page-bg">
        <div className="content-center">
          <div className="survey-container">
            <p
              style={{ textAlign: "center", color: "#888", marginTop: "3rem" }}
            >
              Loading your generated survey…
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="page-bg">
        <div className="content-center">
          <div className="survey-container">
            <p
              style={{ textAlign: "center", color: "#888", marginTop: "3rem" }}
            >
              {resumeError ||
                "No survey data found. Please go back and generate a survey."}
            </p>
            {typeof onBack === "function" && (
              <div className="action-bar" style={{ justifyContent: "center" }}>
                <button className="btn-secondary" onClick={onBack}>
                  Go Back
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentSection = survey.sections[activeSection];

  function showToast(type, message) {
    setToast({ type, message, key: Date.now() });
  }

  async function callSurveyApi(method, action, body) {
    const token = Cookies.get("token");
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/generated/survey/${requestId}/${action}`,
      {
        method,
        headers: { "Content-Type": "application/json", token },
        body: JSON.stringify(body),
      },
    );

    let data;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error("Unexpected response from the server.");
    }

    if (!res.ok || !data.success) {
      throw new Error(data.message || "Something went wrong.");
    }
    return data;
  }

  function updateSectionQuestions(updater) {
    setSurvey((prev) => {
      const sections = prev.sections.map((sec, i) => {
        if (i !== activeSection) return sec;
        return { ...sec, questions: updater([...sec.questions]) };
      });
      return { ...prev, sections };
    });
  }

  function updateQuestionById(questionId, updater) {
    updateSectionQuestions((questions) =>
      questions.map((q) => (q.questionId === questionId ? updater(q) : q)),
    );
  }

  async function handleTitleBlur() {
    const trimmed = (survey.title || "").trim();
    if (!trimmed || trimmed === savedTitleRef.current) return;

    try {
      await callSurveyApi("PUT", "update", {
        label: "edit_title",
        data: { title: trimmed },
      });
      savedTitleRef.current = trimmed;
      setSurvey((prev) => ({ ...prev, title: trimmed }));
    } catch (err) {
      showToast("error", err.message || "Couldn't save the survey title.");
    }
  }

  function startEdit(q) {
    editBackupRef.current = JSON.parse(JSON.stringify(q));
    setEditingQuestionId(q.questionId);
    setQuestionErrors((prev) => ({ ...prev, [q.questionId]: null }));
  }

  function cancelEdit(questionId) {
    const backup = editBackupRef.current;
    if (backup) {
      updateQuestionById(questionId, () => backup);
    }
    editBackupRef.current = null;
    setEditingQuestionId(null);
    setQuestionErrors((prev) => ({ ...prev, [questionId]: null }));
  }

  async function saveEdit(q) {
    const hasOptions = OPTION_BEARING_TYPES.includes(q.type);

    const data = { text: q.text, required: !!q.required };
    if (hasOptions) {
      data.options = (q.options || []).map((o) => ({ text: o.text }));
    }
    if (q.type === "multi_select") {
      data.maxSelections = q.maxSelections ?? null;
    }
    if (q.type === "open_text") {
      data.placeholder = q.placeholder || "";
    }
    if (q.type === "rating_1_10") {
      data.scale = q.scale === 5 ? 5 : 10;
    }

    setIsSavingEdit(true);
    setQuestionErrors((prev) => ({ ...prev, [q.questionId]: null }));

    try {
      const res = await callSurveyApi("PUT", "update", {
        label: "edit_question",
        sectionId: currentSection.sectionId,
        questionId: q.questionId,
        data,
      });
      updateQuestionById(q.questionId, () => res.question);
      editBackupRef.current = null;
      setEditingQuestionId(null);
    } catch (err) {
      setQuestionErrors((prev) => ({
        ...prev,
        [q.questionId]: err.message || "Couldn't save this question.",
      }));
    } finally {
      setIsSavingEdit(false);
    }
  }

  function handleSaveEdit() {
    const q = currentSection.questions.find(
      (qq) => qq.questionId === editingQuestionId,
    );
    if (q) saveEdit(q);
  }

  function changeField(questionId, field, value) {
    updateQuestionById(questionId, (q) => ({ ...q, [field]: value }));
  }

  function addOption(questionId) {
    updateQuestionById(questionId, (q) => ({
      ...q,
      options: [
        ...(q.options || []),
        { text: `Option ${(q.options || []).length + 1}` },
      ],
    }));
  }

  function removeOption(questionId, index) {
    updateQuestionById(questionId, (q) => {
      const reason = getRemoveDisabledReason(q, (q.options || []).length);
      if (reason) return q;
      return { ...q, options: (q.options || []).filter((_, i) => i !== index) };
    });
  }

  function optionTextChange(questionId, index, value) {
    updateQuestionById(questionId, (q) => ({
      ...q,
      options: (q.options || []).map((o, i) =>
        i === index ? { ...o, text: value } : o,
      ),
    }));
  }

  function moveRankItem(questionId, fromIndex, toIndex) {
    updateQuestionById(questionId, (q) => {
      const opts = [...(q.options || [])];
      if (toIndex < 0 || toIndex >= opts.length) return q;
      const [moved] = opts.splice(fromIndex, 1);
      opts.splice(toIndex, 0, moved);
      return { ...q, options: opts };
    });
  }

  async function addQuestion(type) {
    setAddingQuestionType(type);
    try {
      const res = await callSurveyApi("PUT", "update", {
        label: "add_question",
        sectionId: currentSection.sectionId,
        data: { type },
      });
      const newQ = res.question;
      updateSectionQuestions((questions) => [...questions, newQ]);
      startEdit(newQ);
    } catch (err) {
      showToast("error", err.message || "Couldn't add the question.");
    } finally {
      setAddingQuestionType(null);
    }
  }

  async function deleteQuestion(questionId) {
    if (currentSection.questions.length <= 1) {
      showToast("error", "A section needs at least 1 question.");
      return;
    }

    setDeletingQuestionId(questionId);
    try {
      await callSurveyApi("DELETE", "delete", {
        label: "delete_question",
        sectionId: currentSection.sectionId,
        questionId,
      });

      if (editingQuestionId === questionId) {
        editBackupRef.current = null;
        setEditingQuestionId(null);
      }
      updateSectionQuestions((questions) =>
        questions
          .filter((q) => q.questionId !== questionId)
          .map((q, i) => ({ ...q, order: i + 1 })),
      );
      setQuestionErrors((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    } catch (err) {
      showToast("error", err.message || "Couldn't delete the question.");
    } finally {
      setDeletingQuestionId(null);
    }
  }

  function handleDragStart(questionId) {
    setDraggedQuestionId(questionId);
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  async function handleDrop(targetQuestionId) {
    if (!draggedQuestionId || draggedQuestionId === targetQuestionId) {
      setDraggedQuestionId(null);
      return;
    }

    const previousQuestions = currentSection.questions;
    const fromIndex = previousQuestions.findIndex(
      (q) => q.questionId === draggedQuestionId,
    );
    const toIndex = previousQuestions.findIndex(
      (q) => q.questionId === targetQuestionId,
    );
    setDraggedQuestionId(null);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...previousQuestions];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const withOrder = reordered.map((q, i) => ({ ...q, order: i + 1 }));

    updateSectionQuestions(() => withOrder);

    try {
      await callSurveyApi("PUT", "reorder", {
        label: "reorder_questions",
        sectionId: currentSection.sectionId,
        questionOrder: withOrder.map((q) => q.questionId),
      });
    } catch (err) {
      updateSectionQuestions(() => previousQuestions);
      showToast("error", err.message || "Couldn't reorder the questions.");
    }
  }

  function handleDragEnd() {
    setDraggedQuestionId(null);
  }

  const handleContinue = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      const finalSurvey = { title: survey.title, sections: survey.sections };

      if (typeof onContinue === "function") {
        onContinue(finalSurvey);
      }

      if (isAuthenticated) {
        savePendingGenerateDraft(getUserId?.(), "audience", {
          requestId,
          surveyName: survey.title,
        });
      }

      try {
        const saved = readLocalSpecs();
        localStorage.setItem(
          SPEC_STORAGE_KEY,
          JSON.stringify({
            ...saved,
            flow: "generated",
            requestId,
            finalSurvey,
            surveyName: survey.title,
          }),
        );
      } catch (_) {}

      navigate("/define-audience", {
        state: {
          flow: "generated",
          requestId,
          surveyName: survey.title,
        },
      });
    } catch (err) {
      setSaveError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="page-bg">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <SurveyFlowHeader
        flow="generated"
        activeStep="generated"
        isAuthenticated={isAuthenticated}
        maxStepReached={maxStepReached}
      />
      <div className="content-center">
        <div className="survey-page-layout">
          <div className="tabs-wrap tabs-wrap--vertical">
            {survey.sections.map((sec, i) => (
              <button
                key={sec.sectionId}
                className={`tab-pill${i === activeSection ? " tab-pill--active" : ""}`}
                onClick={() => setActiveSection(i)}
              >
                {sec.name}
              </button>
            ))}
          </div>
          <div className="survey-container">
            <h1 className="survey-page-title">
              Your survey has been generated successfully.
            </h1>
            <p className="survey-page-subtitle">
              Please review it before proceeding.
            </p>
            <label className="survey-title-label">Survey Title</label>
            <input
              className="survey-title-input"
              type="text"
              value={survey.title}
              onChange={(e) =>
                setSurvey((prev) => ({ ...prev, title: e.target.value }))
              }
              onBlur={handleTitleBlur}
            />
            {survey.introduction && (
              <div className="survey-intro">{survey.introduction}</div>
            )}
            <p className="section-label">{currentSection.name}</p>
            {currentSection.questions.map((q) => (
              <QuestionCard
                key={q.questionId}
                q={q}
                isEditing={editingQuestionId === q.questionId}
                isDragging={draggedQuestionId === q.questionId}
                isSavingEdit={
                  editingQuestionId === q.questionId && isSavingEdit
                }
                isDeleting={deletingQuestionId === q.questionId}
                deleteDisabledReason={
                  currentSection.questions.length <= 1
                    ? "A section needs at least 1 question"
                    : null
                }
                editError={questionErrors[q.questionId]}
                onStartEdit={() => startEdit(q)}
                onCancelEdit={() => cancelEdit(q.questionId)}
                onSaveEdit={handleSaveEdit}
                onDelete={() => deleteQuestion(q.questionId)}
                onChangeField={(field, value) =>
                  changeField(q.questionId, field, value)
                }
                onOptionTextChange={(i, value) =>
                  optionTextChange(q.questionId, i, value)
                }
                onAddOption={() => addOption(q.questionId)}
                onRemoveOption={(i) => removeOption(q.questionId, i)}
                onMoveRankItem={(from, to) =>
                  moveRankItem(q.questionId, from, to)
                }
                dragHandlers={{
                  onDragStart: () => handleDragStart(q.questionId),
                  onDragOver: handleDragOver,
                  onDrop: () => handleDrop(q.questionId),
                  onDragEnd: handleDragEnd,
                }}
              />
            ))}
            <AddQuestionButton
              onAdd={addQuestion}
              disabled={!!addingQuestionType}
            />
            {activeSection === survey.sections.length - 1 &&
              survey.conclusion && (
                <div
                  className="survey-intro"
                  style={{ marginTop: "1.5rem", fontStyle: "italic" }}
                >
                  {survey.conclusion}
                </div>
              )}
            {saveError && (
              <div
                className="error-banner"
                style={{
                  marginTop: "1rem",
                  padding: "0.75rem 1rem",
                  background: "#fff0f0",
                  border: "1px solid #f5c2c2",
                  borderRadius: "8px",
                  color: "#c0392b",
                  fontSize: "0.9rem",
                }}
              >
                {saveError}
              </div>
            )}
            <div className="action-bar">
              {typeof onBack === "function" && !isSaving && (
                <button className="btn-secondary" onClick={onBack}>
                  Go Back
                </button>
              )}
              <button
                className="btn-primary"
                onClick={handleContinue}
                disabled={isSaving}
              >
                {isSaving ? "Saving…" : "Continue"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
